// ------------------------------------------------------------------
// PURE MODULE: elastic_stagger.mod.js (V4 - Master Manager Aligned)
// ------------------------------------------------------------------
import gsap from "gsap";

export function ElasticStagger() {
    let instanceConfig = {}; 
    let proxy = { value: 0 };
    let elementUnits = []; 

    return {
        init(customConfig = {}) {
            instanceConfig = { ...ElasticStagger.defaults, ...customConfig };
            const elements = document.querySelectorAll(instanceConfig.targets);
            
            // =========================================================
            // [UNIVERSAL CORE]: LAZY-BUILD OBSERVER
            // =========================================================
            const buildObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        if (entry.target._buildAnimation) entry.target._buildAnimation(); 
                    } else {
                        if (entry.target._destroyAnimation) entry.target._destroyAnimation();
                    }
                });
            }, { rootMargin: "800px 0px" });

            elementUnits = Array.from(elements).map((el, i) => {
                
                // 1. Find the static wrapper protecting this element
                const parent = el.closest('[data-common]') || el.parentElement;

                // 2. Create a dispatcher on the parent if it doesn't exist yet
                if (!parent._mmDispatcher) {
                    parent._mmDispatcher = [];
                    // Master Manager Hooks: Broadcast state changes to all children
                    parent._setVisibility = (v) => parent._mmDispatcher.forEach(fn => fn.visibility(v));
                    parent._setOverride = (v) => parent._mmDispatcher.forEach(fn => fn.override(v));
                    parent._buildAnimation = () => parent._mmDispatcher.forEach(fn => fn.build());
                    parent._destroyAnimation = () => parent._mmDispatcher.forEach(fn => fn.destroy());
                    
                    // Observe the STATIC parent, not the flying child
                    buildObserver.observe(parent);
                }

                let isBuilt = false;
                let isInView = false; 
                let isPinnedOverride = false;
                let setters = {};
                
                const speed = instanceConfig.speeds[i] ?? instanceConfig.speeds[instanceConfig.speeds.length - 1];

                // 3. Register this specific element's hooks to the parent's dispatcher
                parent._mmDispatcher.push({
                    visibility: (v) => isInView = v,
                    override: (v) => isPinnedOverride = v,
                    build: () => {
                        if (isBuilt) return;
                        if (instanceConfig.props.includes('y')) setters.y = gsap.quickSetter(el, "y", "px");
                        if (instanceConfig.props.includes('opacity')) setters.opacity = gsap.quickSetter(el, "opacity");
                        isBuilt = true;
                    },
                    destroy: () => {
                        if (!isBuilt) return;
                        setters = {};
                        gsap.set(el, { clearProps: "y,opacity" }); // Safe to clear, observer is watching parent!
                        isBuilt = false;
                    }
                });

                // =========================================================
                // [UNIVERSAL CORE / REPLACEABLE SHELL]: THE RENDER LOOP
                // =========================================================
                return (gap) => {
                    if (!isBuilt || !isInView || isPinnedOverride) return; 
                    
                    const drift = gap * speed * instanceConfig.intensity;
                    
                    if (setters.y) setters.y(drift);
                    if (setters.opacity) {
                        const op = gsap.utils.clamp(0, 1, 1 - Math.abs(drift) / instanceConfig.opacitySensitivity);
                        setters.opacity(op);
                    }
                };
            });
            
            if (window.App?.progress) proxy.value = window.App.progress.value;
        },

        // =========================================================
        // [UNIVERSAL CORE]: THE MASTER MOMENTUM DRIVER
        // =========================================================
        update(currentScroll) { 
            if (elementUnits.length === 0) return;
            
            let diff = currentScroll - proxy.value;
            
            if (Math.abs(diff) < 0.1) {
                proxy.value = currentScroll;
                diff = 0;
            } else {
                proxy.value += diff * instanceConfig.lerp;
            }
            
            if (diff === 0) return; 

            const gap = currentScroll - proxy.value; 

            for (let i = 0; i < elementUnits.length; i++) {
                elementUnits[i](gap);
            }
        }
    };
}

ElasticStagger.defaults = {
    targets: ".testcube",
    speeds: [0.05, 0.07, 0.1, 0.15],
    lerp: 0.05,
    intensity: 10,
    props: ['y'], 
    opacitySensitivity: 300 
};